import React, { useEffect, useState, useCallback } from 'react';
import { SECTION_DIAL_ORDER, SECTION_DIAL_CARDS, resolveActiveDialCardId } from '../../utils/sectionDial';
import {
  applySunModeToDocument,
  readSunModePreference,
  writeSunModePreference,
} from '../../utils/sunMode';
import { NAV_SECTION_ICONS } from './navSectionIcons';
import { navProBadgeText } from '../../features/pro/navProBadge';

/**
 * Field-edition mobile section dial (handoff 3 §3).
 * Opens from MORE; Profile is a mid-rail destination.
 */
export function MobileSectionDial({
  open,
  onClose,
  currentPage,
  onNavigate,
  onOpenToolkit,
  onOpenCommunity,
  unreadMessagesTotal = 0,
  unreadClubsBoardTotal = 0,
  hasClubsEntitlement,
  hasToolkitEntitlement,
}) {
  const [sunMode, setSunMode] = useState(() => readSunModePreference());

  useEffect(() => {
    applySunModeToDocument(sunMode);
  }, [sunMode]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const toggleSunMode = useCallback(() => {
    setSunMode((prev) => {
      const next = !prev;
      writeSunModePreference(next);
      return next;
    });
  }, []);

  const activeId = resolveActiveDialCardId(currentPage);

  const handleSelect = useCallback(
    (id) => {
      onClose();
      switch (id) {
        case 'clubs':
          onNavigate('/clubs');
          break;
        case 'hunts':
          onNavigate('/hunts');
          break;
        case 'ground':
          onNavigate('/maps');
          break;
        case 'community':
          if (onOpenCommunity) onOpenCommunity();
          else onNavigate('/hunting-buddies');
          break;
        case 'profile':
          onNavigate('/profile');
          break;
        case 'toolkit':
          if (onOpenToolkit) onOpenToolkit();
          else onNavigate('/toolkit');
          break;
        case 'messages':
          onNavigate('/messages');
          break;
        default:
          break;
      }
    },
    [onClose, onNavigate, onOpenCommunity, onOpenToolkit]
  );

  if (!open) return null;

  return (
    <div className="mobile-section-dial-portal d-md-none">
      <button
        type="button"
        className="mobile-section-dial__backdrop"
        aria-label="Close section dial"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-section-dial-title"
        className="mobile-section-dial"
        data-testid="mobile-section-dial"
        id="mobile-section-dial"
      >
        <div className="mobile-section-dial__header">
          <h2 id="mobile-section-dial-title" className="mobile-section-dial__title">
            Go to
          </h2>
          <span className="mobile-section-dial__rule" aria-hidden="true" />
          <button
            type="button"
            className={`mobile-section-dial__sun${sunMode ? ' mobile-section-dial__sun--on' : ''}`}
            data-testid="sun-mode-toggle"
            aria-pressed={sunMode}
            aria-label={sunMode ? 'Turn sun mode off' : 'Turn sun mode on'}
            onClick={toggleSunMode}
          >
            <span aria-hidden="true">☀</span>
            <span className="mobile-section-dial__sun-label">SUN</span>
          </button>
          <button
            type="button"
            className="mobile-section-dial__close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div
          className="mobile-section-dial__rail"
          role="navigation"
          aria-label="Section dial"
        >
          {SECTION_DIAL_ORDER.map((id) => {
            const card = SECTION_DIAL_CARDS[id];
            const Icon = NAV_SECTION_ICONS[id];
            const active = activeId === id;
            const unreadBadge =
              id === 'messages' && unreadMessagesTotal > 0
                ? unreadMessagesTotal > 99
                  ? '99+'
                  : String(unreadMessagesTotal)
                : id === 'clubs' && unreadClubsBoardTotal > 0
                  ? unreadClubsBoardTotal > 99
                    ? '99+'
                    : String(unreadClubsBoardTotal)
                  : null;
            const proBadge =
              !unreadBadge && id === 'clubs'
                ? navProBadgeText(hasClubsEntitlement)
                : !unreadBadge && id === 'toolkit'
                  ? navProBadgeText(hasToolkitEntitlement)
                  : null;
            const badge = unreadBadge || proBadge;
            const badgeKind = unreadBadge ? 'unread' : proBadge ? 'pro' : null;
            return (
              <button
                key={id}
                type="button"
                className={`mobile-section-dial__card${active ? ' mobile-section-dial__card--active' : ''}`}
                data-testid={`mobile-section-dial-card-${id}`}
                aria-current={active ? 'page' : undefined}
                onClick={() => handleSelect(id)}
              >
                <span className="mobile-section-dial__glyph" aria-hidden="true">
                  {Icon ? <Icon size={16} /> : null}
                </span>
                <span className="mobile-section-dial__card-title">{card.title}</span>
                <span className="mobile-section-dial__card-sub">{card.sub}</span>
                {badge ? (
                  <span
                    className={`mobile-section-dial__badge${badgeKind === 'pro' ? ' mobile-section-dial__badge--pro' : ''}`}
                    data-testid={
                      badgeKind === 'pro' ? `mobile-section-dial-pro-${id}` : undefined
                    }
                  >
                    {badge}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="mobile-section-dial__ticks" aria-hidden="true" />
      </div>
    </div>
  );
}
